import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  BriefcaseBusiness,
  GraduationCap,
  Sparkles,
  FileText,
  Users,
  CheckCircle2,
} from "lucide-react";
import placement from "../../../Assets/Images/placement.png";

const placementBenefits = [
  {
    icon: BriefcaseBusiness,
    title: "Industry Ready",
    text: "Skills built for real opportunities.",
  },
  {
    icon: Users,
    title: "Career Support",
    text: "Guidance from portfolio to interview.",
  },
  {
    icon: GraduationCap,
    title: "Professional Edge",
    text: "Stand out with practical experience.",
  },
];

const careerBenefits = [
  "Portfolio Building",
  "Interview Preparation",
  "Industry Guidance",
  "Career Mentorship",
];

const placementJourney = [
  {
    icon: FileText,
    label: "Resume",
  },
  {
    icon: Users,
    label: "Interview",
  },
  {
    icon: BriefcaseBusiness,
    label: "Placement",
  },
];

const PlacementSection = () => {
  return (
    <section className="relative w-full overflow-hidden bg-white py-8 sm:py-10 md:py-12 lg:py-14 xl:py-16">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-16 h-52 w-52 rounded-full bg-red-50 blur-[85px] sm:h-64 sm:w-64" />
        <div className="absolute -right-32 bottom-0 h-60 w-60 rounded-full bg-blue-50 blur-[95px] sm:h-72 sm:w-72" />
        <div className="absolute left-1/2 top-0 h-44 w-[320px] -translate-x-1/2 rounded-full bg-purple-50/40 blur-[85px] sm:h-56 sm:w-[450px]" />
        <div className="absolute inset-0 bg-gradient-to-br from-red-50/10 via-transparent to-blue-50/20" />
      </div>

      <div className="relative mx-auto w-full max-w-[1600px] px-3.5 sm:px-5 md:px-6 lg:px-8 xl:px-10 2xl:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="mb-5 flex w-full flex-col items-center justify-center text-center sm:mb-7 md:mb-8"
        >
          <div className="mb-2 inline-flex items-center justify-center gap-2 rounded-full border border-red-200 bg-gradient-to-r from-red-50 via-white to-blue-50 px-3 py-1.5 shadow-sm sm:px-3.5">
            <Sparkles
              size={12}
              className="shrink-0 text-[#d6323c]"
              strokeWidth={2}
            />

            <span className="font-serif text-[11px] font-semibold text-[#283650] sm:text-[12px]">
              Career Success
            </span>
          </div>

          <h2 className="w-full max-w-[950px] font-serif text-[28px] font-bold leading-[1.08] tracking-[-0.035em] text-[#171b29] sm:text-[34px] md:text-[42px] lg:text-[48px] xl:text-[52px]">
            Learn today.
            <span className="ml-1.5 bg-gradient-to-r from-[#d63139] via-[#a84261] to-[#304e8c] bg-clip-text text-transparent sm:ml-2">
              Get placed tomorrow.
            </span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.12 }}
          transition={{ duration: 0.65 }}
          className="relative w-full overflow-hidden rounded-[20px] border border-[#e1e4eb] bg-white shadow-[0_12px_35px_rgba(27,38,60,0.08)] sm:rounded-[24px] lg:rounded-[28px]"
        >
          <div className="pointer-events-none absolute -right-20 -top-28 h-64 w-64 rounded-full bg-gradient-to-br from-red-100 via-purple-50 to-blue-100 opacity-60 blur-2xl sm:h-72 sm:w-72" />

          <div className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-gradient-to-tr from-red-50/70 to-transparent blur-3xl" />

          <div className="relative grid w-full lg:grid-cols-[0.9fr_1.1fr]">
            <div className="relative flex min-w-0 flex-col justify-center p-5 text-center sm:p-7 md:p-8 lg:items-start lg:p-8 lg:text-left xl:p-10 2xl:p-12">
              <div className="mb-3 flex items-center justify-center gap-2 sm:mb-3.5 lg:justify-start">
                <div className="h-[2px] w-7 rounded-full bg-gradient-to-r from-[#d5323c] to-[#304e8c] sm:w-9" />

                <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#68758a] sm:text-[10px] sm:tracking-[0.2em]">
                  Placement Advantage
                </span>
              </div>

              <div className="w-full max-w-[560px] font-serif font-bold tracking-[-0.045em]">
                <div className="flex items-end justify-center gap-1 leading-none lg:justify-start">
                  <span className="text-[50px] font-bold leading-[0.78] tracking-[-0.065em] text-[#182033] sm:text-[58px] md:text-[62px] lg:text-[60px] xl:text-[66px]">
                    100
                  </span>

                  <span className="mb-[3px] text-[23px] font-bold leading-none text-[#d5323c] sm:mb-[5px] sm:text-[25px] md:text-[27px] lg:mb-[6px] lg:text-[29px] xl:text-[32px]">
                    %
                  </span>

                  <span className="mb-[2px] whitespace-nowrap text-[23px] font-bold leading-none tracking-[-0.04em] text-[#192238] sm:mb-[4px] sm:text-[28px] md:text-[32px] lg:mb-[5px] lg:text-[34px] xl:text-[37px]">
                    Your Career.
                  </span>
                </div>

                <div className="mt-1 whitespace-nowrap text-[25px] font-bold leading-[0.95] tracking-[-0.04em] sm:mt-1.5 sm:text-[31px] md:text-[35px] lg:mt-2 lg:text-[38px] xl:text-[42px]">
                  <span className="bg-gradient-to-r from-[#d63139] via-[#a84261] to-[#304e8c] bg-clip-text text-transparent">
                    Our Commitment.
                  </span>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-center lg:justify-start">
                <div className="h-[3px] w-9 rounded-l-full bg-[#d5323c] sm:w-11" />
                <div className="h-[3px] w-6 bg-[#8b4662] sm:w-8" />
                <div className="h-[3px] w-9 rounded-r-full bg-[#304e8c] sm:w-11" />
              </div>

              <p className="mx-auto mt-3 w-full max-w-[510px] text-center text-[12px] leading-[1.55] text-[#59677d] sm:mt-3.5 sm:text-[13px] md:text-[14px] lg:mx-0 lg:text-left xl:text-[15px]">
                Build practical skills, gain valuable experience, and prepare
                yourself for new opportunities with structured learning and
                professional guidance.
              </p>

              <div className="mx-auto mt-3 flex w-full max-w-[510px] items-center gap-2.5 rounded-xl border border-[#ebe0e2] bg-gradient-to-r from-[#fff7f7] via-white to-[#f5f7fc] px-3 py-2 text-left sm:mt-3.5 sm:gap-3 sm:px-3.5 sm:py-2.5 lg:mx-0">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#d63139] to-[#304e8c] shadow-sm sm:h-8 sm:w-8">
                  <ArrowUpRight
                    size={14}
                    className="text-white sm:h-[15px] sm:w-[15px]"
                    strokeWidth={2.4}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#8a5260] sm:text-[9px] sm:tracking-[0.14em]">
                    Career Launch Support
                  </p>

                  <p className="mt-0.5 text-[10px] font-medium leading-4 text-[#66748a] sm:text-[11px]">
                    From portfolio building to your next opportunity.
                  </p>
                </div>
              </div>

              <div className="mx-auto mt-3.5 grid w-full max-w-[510px] grid-cols-2 gap-x-3 gap-y-2 sm:mt-4 sm:gap-x-5 sm:gap-y-2.5 lg:mx-0">
                {careerBenefits.map((item) => (
                  <div
                    key={item}
                    className="flex min-w-0 items-center gap-1.5 text-left text-[9px] font-semibold text-[#45556e] sm:gap-2 sm:text-[12px] md:text-[13px]"
                  >
                    <CheckCircle2
                      size={13}
                      className="shrink-0 text-[#d5323c] sm:h-4 sm:w-4"
                      strokeWidth={2.4}
                    />

                    <span className="min-w-0 truncate">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 w-full max-w-[510px] sm:mt-6 lg:mt-7">
                <div className="mb-2.5 flex items-center justify-center gap-2 lg:justify-start">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#d63139]" />

                  <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#59677d] sm:text-[10px] sm:tracking-[0.16em]">
                    Your Placement Journey
                  </span>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 lg:justify-start">
                  {placementJourney.map((step, index) => {
                    const Icon = step.icon;

                    return (
                      <React.Fragment key={step.label}>
                        <motion.div
                          whileHover={{ y: -3 }}
                          transition={{ duration: 0.2 }}
                          className="group flex shrink-0 cursor-default items-center gap-1.5 rounded-full border border-[#e5e9f0] bg-white px-2.5 py-1.5 shadow-sm transition-all duration-300 hover:border-[#d63139]/30 hover:shadow-md sm:gap-2 sm:px-3 sm:py-2"
                        >
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#d63139] to-[#304e8c] text-white sm:h-7 sm:w-7">
                            <Icon
                              size={12}
                              className="transition-transform duration-300 group-hover:scale-110 sm:h-[13px] sm:w-[13px]"
                            />
                          </span>

                          <span className="text-[9px] font-semibold text-[#26354d] sm:text-[10px] md:text-[11px]">
                            {step.label}
                          </span>
                        </motion.div>

                        {index < placementJourney.length - 1 && (
                          <ArrowUpRight
                            size={12}
                            className="shrink-0 text-[#aab3c1] sm:h-[14px] sm:w-[14px]"
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="relative min-h-[260px] p-2 sm:min-h-[330px] sm:p-3 md:min-h-[390px] lg:min-h-[500px]">
              <div className="relative h-full min-h-[260px] overflow-hidden rounded-[17px] sm:min-h-[330px] sm:rounded-[20px] md:min-h-[390px] lg:min-h-[500px] lg:rounded-[22px]">
                <img
                  src={placement}
                  alt="Students preparing for their professional careers"
                  className="absolute inset-0 h-full w-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/90 via-[#111827]/20 to-transparent" />

                <div className="absolute inset-0 bg-gradient-to-br from-[#d6323c]/10 via-transparent to-[#304e8c]/20" />

                <div className="absolute left-3 top-3 sm:left-5 sm:top-5 lg:left-6 lg:top-6">
                  <div className="flex items-center gap-1.5 rounded-full border border-white/20 bg-black/20 px-2.5 py-1 backdrop-blur-md sm:gap-2 sm:px-3 sm:py-1.5">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#ff6a70]" />

                    <span className="text-[7px] font-bold uppercase tracking-[0.14em] text-white sm:text-[9px] sm:tracking-[0.16em]">
                      Career Ready
                    </span>
                  </div>
                </div>

                <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 lg:bottom-7 lg:left-7 lg:right-7">
                  <p className="mb-1.5 text-[7px] font-bold uppercase tracking-[0.16em] text-[#ff9ba0] sm:mb-2 sm:text-[9px] sm:tracking-[0.18em]">
                    From Learning To Career
                  </p>

                  <h4 className="max-w-[480px] font-serif text-[19px] font-bold leading-[1.08] text-white sm:text-[25px] md:text-[29px] lg:text-[32px]">
                    Your skills deserve
                    <br />
                    <span className="text-[#ff9ba0]">
                      the right opportunity.
                    </span>
                  </h4>

                  <p className="mt-2 max-w-[430px] text-[9px] leading-4 text-white/70 sm:mt-3 sm:text-[11px] sm:leading-5 md:text-[12px]">
                    Practical learning, professional confidence and guidance
                    designed around your career goals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-3.5 grid w-full grid-cols-3 gap-1.5 sm:mt-4 sm:gap-3 md:gap-4 lg:gap-4">
          {placementBenefits.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                }}
                whileHover={{ y: -3 }}
                className="group flex min-w-0 flex-col items-center justify-center gap-1.5 rounded-[12px] border border-[#e3e6ec] bg-white px-1.5 py-2.5 text-center shadow-[0_5px_18px_rgba(30,40,60,0.04)] transition-all duration-300 hover:border-[#d9b9bd] hover:shadow-[0_9px_22px_rgba(30,40,60,0.08)] sm:flex-row sm:justify-start sm:gap-3 sm:rounded-[16px] sm:px-3 sm:py-3 md:gap-4 md:rounded-[18px] md:px-4 md:py-3.5"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-gradient-to-br from-red-50 to-blue-50 sm:h-10 sm:w-10 sm:rounded-[11px] md:h-11 md:w-11 md:rounded-[13px]">
                  <Icon
                    size={15}
                    className="text-[#d5323c] sm:h-[17px] sm:w-[17px] md:h-[19px] md:w-[19px]"
                    strokeWidth={1.9}
                  />
                </div>

                <div className="min-w-0">
                  <h5 className="truncate font-serif text-[9px] font-bold text-[#1b2438] sm:text-[12px] md:text-[16px]">
                    {item.title}
                  </h5>

                  <p className="mt-0.5 hidden text-[10px] leading-4 text-[#748095] sm:block md:text-[11px]">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PlacementSection;